import { ADMIN_HELP_ARTICLES } from "@/lib/product-knowledge/admin-help-articles";
import { INTEGRATION_HELP_ARTICLES } from "@/lib/product-knowledge/integration-articles";
import { searchHelpArticlesRanked } from "@/lib/product-knowledge/search-help";

export interface HelpArticle {
  id: string;
  title: string;
  section: string;
  sectionIcon: string;
  keywords: string[];
  content: string;
  url?: string;
  summary?: string;
  faqs?: { question: string; answer: string }[];
}

const CORE_HELP_ARTICLES: HelpArticle[] = [
  {
    id: "register",
    title: "Crear cuenta y primer acceso",
    section: "Primeros pasos",
    sectionIcon: "🚀",
    keywords: ["registro", "cuenta", "login", "contratar", "plan"],
    content: `
1. Elegí un plan (desde la landing o directo en **/contratar**)
2. Completá los datos de tu **negocio** (nombre y dirección)
3. Creá tu **cuenta de acceso** con email y contraseña
4. Confirmá el pago para activar la tienda
5. Seguí el **onboarding** de 5 pasos
`,
  },
  {
    id: "onboarding",
    title: "Completar el onboarding",
    section: "Primeros pasos",
    sectionIcon: "🚀",
    keywords: ["onboarding", "setup", "inicio"],
    url: "/app/onboarding",
    content: `
Pasos: **Negocio** (nombre y slug) → **Sucursal** → **Categorías** → **Productos** → **Pagos**.

Retomalo desde el banner **Setup incompleto** en el menú lateral.
`,
  },
  {
    id: "dashboard",
    title: "Panel de inicio",
    section: "Panel",
    sectionIcon: "🏠",
    keywords: ["inicio", "dashboard", "resumen"],
    url: "/app/dashboard",
    content: `
Ves pedidos de hoy, pendientes, ingresos del mes y últimos pedidos.

Es el punto de partida cada mañana antes de ir a **Pedidos**.
`,
  },
  {
    id: "public-availability",
    title: "Pausar la tienda pública (Online / Offline)",
    section: "Operación",
    sectionIcon: "⚙️",
    keywords: ["online", "offline", "pausar", "tienda", "público", "mantenimiento", "disponibilidad"],
    content: `
En el **header del panel** (móvil y escritorio) tenés el interruptor **Online ↔ Offline**.

- **Offline:** los clientes ven una pantalla cuidada con tu marca y un botón de WhatsApp. No pueden comprar ni ver el catálogo.
- **Online:** la tienda vuelve al instante.

**No afecta** tu panel: vos, tu equipo y el super admin en modo vista siguen operando con normalidad (pedidos, productos, precios).

Usalo cuando actualizás precios, reorganizás el catálogo o hacés cambios que no querés que vean los clientes hasta terminar.
`,
  },

  {
    id: "leads-age-gate",
    title: "Leads y verificación de edad",
    section: "Tienda pública",
    sectionIcon: "🌐",
    keywords: ["leads", "email", "age gate", "edad", "popup", "cumplimiento", "csv"],
    url: "/app/leads",
    content: `
## Captura de leads
1. Andá a **Experiencia → Cumplimiento** y activá el popup de leads.
2. Configurá título, CTA y texto legal.
3. Los emails aparecen en **Leads** (/app/leads).
4. Descargá el **CSV** para usarlos en tu herramienta de marketing (todavía no enviamos campañas desde Structura).

## Age gate
1. En **Cumplimiento**, activá la verificación de edad mínima.
2. Definí la edad (ej. 18) y el mensaje.
3. Es una **autodeclaración** del visitante — no reemplaza controles legales.
4. Si registrás atestaciones, quedan en la base para auditoría interna.
`,
  },

  {
    id: "new-order",
    title: "Gestionar un pedido nuevo",
    section: "Pedidos",
    sectionIcon: "🛒",
    keywords: ["pedido", "confirmar", "estado"],
    url: "/app/pedidos",
    content: `
1. Andá a **Pedidos**
2. Abrí el pedido
3. Revisá ítems, entrega y pago
4. Cambiá el estado (confirmado → preparación → listo → entregado)

💡 Con productos pesables, pasá a **Pesaje** antes del cobro final.
`,
  },
  {
    id: "order-states",
    title: "Estados del pedido",
    section: "Pedidos",
    sectionIcon: "🛒",
    keywords: ["estado", "pendiente", "entregado", "cancelado"],
    url: "/app/pedidos",
    content: `
Flujo habitual:
**PENDIENTE** → **CONFIRMADO** → **EN_PREPARACION** → **LISTO** / **LISTO_RETIRO** / **EN_CAMINO** → **ENTREGADO**.

Con pesaje: **AJUSTADO_PESADO** → **PENDIENTE_PAGO** → **PAGADO**.
`,
  },
  {
    id: "weighing",
    title: "Pesaje y cobro final",
    section: "Pesaje",
    sectionIcon: "⚖️",
    keywords: ["pesaje", "kilo", "pesables", "balanza", "pesaje_module"],
    url: "/app/pesaje",
    content: `
1. Elegí el pedido en **Pesaje**
2. Cargá **peso real** por ítem
3. Guardá → total actualizado
4. **Generá link** MP y envialo al cliente
`,
  },
  {
    id: "weighing-link",
    title: "Link de pago post-pesaje",
    section: "Pesaje",
    sectionIcon: "⚖️",
    keywords: ["link", "mercadopago", "pago"],
    url: "/app/pesaje",
    content: `
Después de guardar pesos, usá **Generar link de pago**.

El cliente paga el monto exacto. Verificá credenciales en **Configuración → Pagos**.
`,
  },
  {
    id: "categories",
    title: "Categorías del catálogo",
    section: "Productos",
    sectionIcon: "📦",
    keywords: ["categoría", "orden", "catálogo"],
    url: "/app/categorias",
    content: `
En **Categorías** creás y ordenás secciones de la tienda.

Cada producto debe tener categoría para mostrarse bien en \`/market/tu-slug\`.
`,
  },
  {
    id: "products",
    title: "Cargar productos",
    section: "Productos",
    sectionIcon: "📦",
    keywords: ["producto", "precio", "foto", "stock", "catalog_public", "stock_management"],
    url: "/app/productos",
    content: `
**Nuevo producto**: nombre, categoría, foto, precio.

- **Unidad**: precio fijo
- **Peso**: precio por kg + flujo de pesaje
- **Tags**: Oferta, Destacado, etc.
`,
  },
  {
    id: "inventory-hub",
    title: "Inventario y movimientos de stock",
    section: "Operación",
    sectionIcon: "📦",
    keywords: ["inventario", "stock", "movimientos", "csv", "devoluciones", "hub"],
    url: "/app/inventario",
    content: `
**Inventario** agrupa operaciones de stock en un hub con accesos rápidos:

1. Andá a **Inventario** en el menú (o bottom nav en mobile)
2. Elegí la tarjeta: **Movimientos**, **Importar/exportar CSV** o **Devoluciones**
3. En mobile, las tarjetas reemplazan filas de tabs densas

💡 Para ajustar precios masivos o importar catálogo, usá **Catalogación** (\`/app/catalogacion\`).
`,
  },
  {
    id: "catalogacion-hub",
    title: "Catalogación (precios e importación)",
    section: "Productos",
    sectionIcon: "📦",
    keywords: ["catalogación", "precios", "importar", "csv", "bulk"],
    url: "/app/catalogacion",
    content: `
Desde **Catalogación** accedés a:

- **Ajuste masivo de precios** (\`/app/catalogacion/precios\`)
- **Importar productos** por CSV (\`/app/catalogacion/importar\`)

En mobile verás tarjetas; en desktop, el mismo hub con navegación lateral.
`,
  },
  {
    id: "price-lists",
    title: "Listas de precios múltiples",
    section: "Productos",
    sectionIcon: "📦",
    keywords: ["listas de precios", "price lists", "mayorista", "b2b", "online", "pos"],
    url: "/app/catalogacion/listas-precios",
    content: `
Gestioná varias listas de precios (online, mayorista/B2B, POS, etc.) sin editar cada producto uno por uno. Disponible desde el plan Business.

**Crear una lista**: **Nueva lista** → nombre → **Crear**.

**Agregar productos**: dentro de la lista, elegí productos con el selector y definí, por producto, uno de estos modos de precio:

- **Prod**: usa el precio del producto tal cual (sin cambios)
- **Fijo**: un precio fijo específico para esta lista
- **Desc%**: descuento porcentual sobre el precio del producto
- **Rec%**: recargo porcentual sobre el costo

**Usos de una lista**:
- **Usar en tienda online**: marca la lista como la que resuelve los precios de tu storefront público.
- **Usar en POS**: marca una lista específica para caja — el POS filtra el catálogo a solo esos productos. Si no asignás ninguna, el POS **hereda automáticamente la lista online** (badge "POS (heredado)").
- **Duplicar**: clona la lista completa (nombre y precios) para partir de una existente.
- **Exportar CSV**: descarga la lista con sus precios.

Solo puede haber una lista online y una lista POS activas a la vez — asignar una nueva reemplaza la anterior. Si quitás la lista POS (**Quitar de POS**), la caja vuelve a heredar la lista online.
`,
  },
  {
    id: "brands",
    title: "Marcas de productos",
    section: "Productos",
    sectionIcon: "📦",
    keywords: ["marca", "brand", "fabricante"],
    url: "/app/marcas",
    content: `
1. Andá a **Marcas** (\`/app/marcas\`)
2. **Nueva marca** → nombre y guardar
3. Asigná la marca al crear o editar un producto

El bookmark legacy \`/app/catalogacion/marcas\` redirige a esta pantalla.
`,
  },
  {
    id: "storefront",
    title: "Tu tienda pública",
    section: "Tienda pública",
    sectionIcon: "🌐",
    keywords: ["tienda", "url", "slug", "market", "catalog_public", "orders_online"],
    url: "/app/settings",
    content: `
URL: \`/market/tu-slug\`

Ajustá apariencia en **Configuración → Tienda** y compartí el link desde **Publicar**.
`,
  },
  {
    id: "widget",
    title: "Widget en tu web",
    section: "Tienda pública",
    sectionIcon: "🌐",
    keywords: ["widget", "embed", "iframe", "widget_embed"],
    url: "/app/settings",
    content: `
En **Configuración → Publicar** copiás el código para embeber en WordPress o tu sitio.

Probá en una página de prueba antes de producción.
`,
  },
  {
    id: "b2b-portal",
    title: "Portal institucional B2B",
    section: "B2B",
    sectionIcon: "🏫",
    keywords: ["institucional", "colegio", "viandas", "empresa", "institutional_orders"],
    url: "/app/produccion",
    content: `
1. **Portal B2B**: usuarios y menús
2. Clientes en \`/institucional/tu-slug\`
3. Pedidos en **B2B / Mayorista**
`,
  },
  {
    id: "b2b-menus",
    title: "Menús B2B por fecha",
    section: "B2B",
    sectionIcon: "🏫",
    keywords: ["menú", "fecha", "planificación"],
    url: "/app/settings",
    content: `
Definí menús por día o período en **Configuración → Portal B2B**.

Los usuarios institucionales solo ven lo publicado para su fecha.
`,
  },
  {
    id: "branches",
    title: "Multi-sucursal",
    section: "Operación",
    sectionIcon: "🏪",
    keywords: ["sucursal", "retiro", "domicilio", "multi_location"],
    url: "/app/sucursales",
    content: `
Cada sucursal: dirección, horarios, retiro y/o domicilio.

El cliente elige sucursal en checkout si tenés más de una.
`,
  },
  {
    id: "metrics",
    title: "Métricas y ventas",
    section: "Operación",
    sectionIcon: "📊",
    keywords: ["métricas", "ventas", "ingresos", "analytics_advanced"],
    url: "/app/metrics",
    content: `
Compará períodos, ticket promedio y volumen.

Útil para picos de fin de semana o campañas.
`,
  },
  {
    id: "payments",
    title: "Configurar pagos",
    section: "Configuración",
    sectionIcon: "💳",
    keywords: ["mercadopago", "transferencia"],
    url: "/app/settings",
    content: `
**Configuración → Pagos**:
- Mercado Pago (token + public key)
- Transferencia (alias/CBU)

Domicilio: suele exigir prepago (Políticas).
`,
  },
  {
    id: "delivery",
    title: "Entrega y WhatsApp",
    section: "Configuración",
    sectionIcon: "🚚",
    keywords: ["entrega", "domicilio", "retiro", "whatsapp"],
    url: "/app/settings",
    content: `
**Entrega**: solo retiro, domicilio o ambos; radio en km.

Plantilla WhatsApp con \`{{id}}\` del pedido.
`,
  },
  {
    id: "whatsapp",
    title: "WhatsApp automático",
    section: "Integraciones",
    sectionIcon: "💬",
    keywords: ["whatsapp", "meta", "twilio"],
    url: "/app/settings",
    content: `
**Mensajería → WhatsApp**: conectá Kapso (recomendado), Meta o Twilio con tu cuenta.

Recibí pedidos directamente en tu WhatsApp cuando entra un pedido nuevo (alerta al dueño).
`,
  },
  {
    id: "email-notif",
    title: "Emails al cliente",
    section: "Notificaciones",
    sectionIcon: "✉️",
    keywords: ["email", "resend", "confirmación"],
    url: "/app/settings",
    content: `
**Notificaciones**: eventos de pedido y plantillas.

Requiere SMTP/Resend configurado en el servidor.
`,
  },
  {
    id: "push",
    title: "Notificaciones push",
    section: "Notificaciones",
    sectionIcon: "🔔",
    keywords: ["push", "onesignal", "alerta"],
    url: "/app/dashboard",
    content: `
Aceptá permisos en el navegador en \`/app\`.

Alertas de pedidos nuevos y pagos (OneSignal).
`,
  },
  {
    id: "seo",
    title: "SEO de la tienda",
    section: "Configuración",
    sectionIcon: "🔍",
    keywords: ["seo", "google", "meta"],
    url: "/app/settings",
    content: `
**SEO**: título, descripción e imagen para redes y buscadores.
`,
  },
  {
    id: "team",
    title: "Equipo y permisos",
    section: "Configuración",
    sectionIcon: "👥",
    keywords: ["equipo", "staff", "invitar"],
    url: "/app/settings",
    content: `
**Equipo**: invitá staff por email.

El dueño controla quién ve pedidos, pesaje o configuración.
`,
  },
  {
    id: "publish",
    title: "Publicar y compartir",
    section: "Configuración",
    sectionIcon: "📣",
    keywords: ["publicar", "qr", "link"],
    url: "/app/settings",
    content: `
**Publicar**: URL, widget, QR y checklist.

Compartí por WhatsApp, Instagram o cartel en el local.
`,
  },
  {
    id: "faq-cancel",
    title: "Cancelar un pedido",
    section: "FAQ",
    sectionIcon: "❓",
    keywords: ["cancelar", "anular"],
    url: "/app/pedidos",
    content: `
Desde el panel podés pasar a **CANCELADO** si la operación lo permite.

El cliente sigue las **Políticas** de tu tienda.
`,
  },

  // ── Merchant y personalización ────────────────────────────────────────────

  {
    id: "personalizacion-por-producto",
    title: "Personalización por producto",
    section: "Productos",
    sectionIcon: "📦",
    keywords: ["personalización", "merchant", "estampado", "bordado", "is_personalizable", "configurador"],
    url: "/app/productos",
    content: `
Podés marcar cada producto como **"Admite personalización"** para que el cliente vea el paso de diseño durante el checkout.

### Cómo activarlo
1. Andá a **Productos** y abrí el producto que querés personalizar.
2. Buscá la opción **"Admite personalización"** y activala.
3. Guardá el producto.

### Qué pasa en la tienda
Cuando el cliente tiene en el carrito al menos un producto con personalización activa, aparece el **paso de diseño** en el checkout: elige zonas, sube su arte y deja notas.

> Si el producto **no** tiene esta opción activada, el paso de personalización nunca aparece para ese ítem — incluso si el negocio tiene el módulo Merchant habilitado.

### Requisito
El módulo **Merchant — Personalización** debe estar activo en tu plan. Si no ves la opción, contactá a soporte.
`,
  },
  {
    id: "nicho-catalogo",
    title: "Nicho de negocio y catálogo",
    section: "Productos",
    sectionIcon: "📦",
    keywords: [
      "nicho",
      "perfil",
      "etiquetas",
      "tags",
      "sin tacc",
      "vegano",
      "por peso",
      "tipo negocio",
      "non-food",
    ],
    url: "/app/settings",
    content: `
El **perfil de negocio** que configuraste en **Configuración → Negocio** determina qué opciones de catálogo ves en el panel.

### Etiquetas (tags) de productos
- **Negocios food** (Rotisería, Mercado, Dietética, Restaurante): ven todas las etiquetas, incluyendo **Sin TACC**, **Vegano**, **Vegetariano**, etc.
- **Negocios non-food** (Indumentaria, Bazar, Productos digitales, Catálogo, Farmacia): solo ven las etiquetas generales: **Oferta**, **Destacado**, **Lo más pedido**, **Nuevo**.

### Venta por peso
El tipo de venta **"Por peso"** (kg) solo aparece en el formulario de productos para perfiles food. En negocios non-food solo se muestra **"Por unidad"**, que es el único tipo de venta relevante para esos rubros.

### Módulo de Pesaje
El card **Pesaje** en el hub de Órdenes solo aparece para negocios food. Los negocios non-food no lo ven.

> Para cambiar el perfil, andá a **Configuración → Negocio → Tipo de negocio**.
`,
  },
  {
    id: "merchant-cotizaciones",
    title: "Merchant — Cotizaciones",
    section: "Operación",
    sectionIcon: "🏪",
    keywords: ["merchant", "cotización", "estampado", "bordado", "personalización", "quote", "operaciones"],
    url: "/app/operaciones",
    content: `
El panel de **Cotizaciones Merchant** es donde gestionás los pedidos de personalización (estampados, bordados) que llegan desde la tienda.

### Cómo acceder
1. Andá al hub **Operaciones** desde el menú lateral.
2. Hacé clic en la tarjeta **Cotizaciones** (aparece solo si tenés el módulo Merchant activo en tu plan).

### Qué podés hacer
- Ver todas las solicitudes de cotización enviadas por clientes.
- Revisar el detalle: prendas, zonas, notas y archivos de diseño del cliente.
- **Cotizar**: ingresar el precio y tiempo de entrega.
- El cliente recibe un email con la cotización y un link para **aprobar o pedir recotización**.
- Al aprobar, se genera la orden en el sistema.

> El link de cotización expira según el **plazo de vigencia** configurado en **Catalogación → Config. Merchant**.
`,
  },
  {
    id: "merchant-config",
    title: "Merchant — Configuración",
    section: "Productos",
    sectionIcon: "📦",
    keywords: ["merchant", "configuración", "zonas", "prendas", "tipos", "vigencia", "plazo", "catalogación"],
    url: "/app/catalogacion",
    content: `
La **Configuración Merchant** es donde definís cómo funciona el módulo de personalización para tu negocio.

### Cómo acceder
1. Andá al hub **Catalogación** desde el menú lateral.
2. Hacé clic en la tarjeta **Config. Merchant** (aparece solo si tenés el módulo activo en tu plan).

### Qué podés configurar
- **Zonas habilitadas** por tipo de prenda (frente, dorso, manga, etc.).
- **Vigencia de cotizaciones**: cuántos días tiene el cliente para aprobar antes de que expire.
- **Plazo de producción**: tiempo estimado que le mostrás al cliente en la cotización.

> Los cambios se aplican a partir de las próximas solicitudes. Los quotes ya enviados no se modifican.
`,
  },

  // ── Commerce Evolution (Fases A–E) ─────────────────────────────────────────

  {
    id: "variantes-productos",
    title: "Variantes de producto (talle, color, tamaño…)",
    section: "Productos",
    sectionIcon: "📦",
    keywords: ["variante", "talle", "color", "tamaño", "sku", "opciones"],
    url: "/app/productos",
    content: `
Un producto puede tener hasta **4 ejes de variante** (ej: Talle + Color).

### Cómo configurar
1. Abrí el producto → sección **Catálogo avanzado**.
2. Agregá los ejes con sus valores (ej: "Talle": S, M, L, XL).
3. Hacé clic en **Generar faltantes** para crear automáticamente todas las combinaciones.
4. Editá precio y stock para cada variante.

### En la tienda
Los clientes ven los selectores antes de agregar al carrito. Si eligen una variante sin stock, el botón muestra "Agotado".
`,
  },
  {
    id: "extras-addons",
    title: "Extras y adicionales (addons)",
    section: "Productos",
    sectionIcon: "📦",
    keywords: ["extra", "addon", "adicional", "ingrediente", "modificador", "cheddar", "papas"],
    url: "/app/productos",
    content: `
Los **extras** son productos reutilizables (cheddar, papas, salsa) que se suman a otros productos.

### Paso a paso
1. Creá el producto extra (ej: "Cheddar") y marcalo como **"Es un extra / adicional"** en Catálogo avanzado.
2. Abrí el producto principal (ej: "Hamburguesa") → Catálogo avanzado → **Extras permitidos**.
3. Vinculá los extras habilitados.

En el checkout el cliente elige cuántos extras agrega. El total se actualiza en tiempo real.

> El precio de los extras siempre se toma del catálogo del servidor — el cliente no puede manipularlo.
`,
  },
  {
    id: "control-stock",
    title: "Control de stock por producto",
    section: "Productos",
    sectionIcon: "📦",
    keywords: ["stock", "inventario", "agotado", "control", "track"],
    url: "/app/productos",
    content: `
Por defecto, los productos son **a demanda** (no se agotan). Para controlar stock:

1. Abrí el producto → **Catálogo avanzado** → activá **Controlar stock**.
2. Ingresá el stock inicial en el campo "Stock".
3. Cuando el stock llega a 0, la tienda muestra "Agotado" automáticamente.

También podés definir un **stock mínimo** para recibir alertas y sugerencias de recompra en **Operaciones**.
`,
  },
  {
    id: "comportamiento-negocio",
    title: "Comportamiento por tipo de negocio",
    section: "Tienda pública",
    sectionIcon: "🌐",
    keywords: ["comportamiento", "nicho", "fast food", "quick add", "galería", "filtros", "tipo negocio"],
    url: "/app/settings",
    content: `
Cada tipo de negocio activa comportamientos diferentes en la tienda:

| Tipo | Agregar desde el listado | Adicionales | Varias fotos | Filtros |
|------|-----------|--------|-------------|---------|
| Fast food | ✓ | ✓ | — | — |
| Moda | — | — | ✓ | ✓ |
| Dietética | ✓ | — | ✓ | ✓ |
| Farmacia | ✓ | — | — | ✓ |

### Personalizar
En **Configuración → Cómo compra el cliente** podés activar o desactivar cada comportamiento. El diseño visual (colores, home, bloques) se edita en **Experiencia**.
`,
  },
  {
    id: "cuenta-cliente",
    title: "Cuentas de clientes en la tienda",
    section: "Tienda pública",
    sectionIcon: "🌐",
    keywords: ["cuenta", "cliente", "login", "registro", "dirección", "historial", "b2c"],
    url: "/app/settings",
    content: `
Tus clientes pueden crear su propia cuenta en tu tienda (disponible en planes Pro+).

### Qué pueden hacer los clientes
- **Registrarse y entrar** con email y contraseña.
- **Guardar direcciones** de entrega.
- **Ver historial de pedidos** (incluyendo pedidos previos guest con el mismo email).

### Cómo acceden
En la tienda pública, el ícono de persona en el header lleva a \`/market/tu-slug/cuenta\`.

### Beneficio operativo
Los pedidos de clientes logueados se vinculan a su cuenta, lo que permite visualizar el historial completo y en el futuro aplicar loyalty automáticamente.
`,
  },
  {
    id: "loyalty-puntos",
    title: "Programa de puntos (loyalty)",
    section: "Fidelización",
    sectionIcon: "⭐",
    keywords: ["loyalty", "puntos", "fidelización", "descuento", "canjear", "acumular"],
    url: "/app/fidelizacion",
    content: `
Tus clientes acumulan puntos en cada compra y los canjean por descuento (disponible en planes Business).

### Configurar desde el panel
1. Ir a **Fidelización** en el menú lateral.
2. Activar **Programa de puntos**.
3. Definir:
   - **Puntos por $1 gastado** (ej: 0.1 = 1 punto cada $10).
   - **Valor de 1 punto** (ej: $1 = 1 punto vale $1).
   - **Mínimo para canjear** (ej: 100 puntos).

### Cómo funciona
- Los puntos se **acumulan automáticamente** cuando la orden pasa a ENTREGADO.
- Si el cliente **cancela o devuelve**, los puntos se revierten.
- En el checkout, el cliente logueado ve sus puntos disponibles y puede canjearlos.
`,
  },
  {
    id: "gift-cards",
    title: "Gift cards",
    section: "Fidelización",
    sectionIcon: "⭐",
    keywords: ["gift card", "tarjeta regalo", "código", "descuento", "saldo"],
    url: "/app/fidelizacion",
    content: `
Emitís gift cards con código único que los clientes usan como descuento en el checkout (disponible en planes Business). También podés **venderlas como producto** en la categoría **Tarjetas regalo** (montos por defecto $20–$100).

### Vender en la tienda
1. Plan **Business** + Mercado Pago configurado.
2. La tienda crea automáticamente la categoría **Tarjetas regalo** y productos Gift Card Basic, Gold, etc.
3. El cliente entra a la PDP → **Comprar y regalar** → completa datos y nominación → pago online.
4. Al confirmarse el pago se genera el código y se envía por email al destinatario y al comprador.

### Emitir manualmente (panel)
1. Ir a **Fidelización** → sección Gift cards.
2. Ingresar monto y (opcional) email del destinatario.
3. Hacer clic en **Emitir**. Se genera un código tipo \`GIFT-XXXX-XXXX\`.
4. Compartí el código por WhatsApp, email o imprimilo.

### En el checkout
El cliente ingresa el código. El saldo se descuenta del total y, si sobra, queda disponible para otro pedido.

### Gestión
Podés ver el saldo de cada gift card y deshabilitarlas desde el panel.
`,
  },
  {
    id: "estados-orden",
    title: "Estados y ciclo de vida de un pedido",
    section: "Pedidos",
    sectionIcon: "🛒",
    keywords: ["estado", "flujo", "ciclo", "confirmado", "entregado", "devuelto", "cancelado", "reembolsado"],
    url: "/app/pedidos",
    content: `
El ciclo completo de un pedido en Structura Market:

\`\`\`
PENDIENTE → CONFIRMADO → EN_PREPARACION → LISTO → ENTREGADO
                                        ↓           ↓
                                    EN_CAMINO    DEVUELTO → REEMBOLSADO
\`\`\`

También puede ir a **CANCELADO** desde cualquier estado activo, y luego a **REEMBOLSADO**.

### Efectos automáticos
| Transición | Qué pasa |
|------------|----------|
| → ENTREGADO | Acumula puntos de loyalty del cliente |
| → CANCELADO | Libera el stock reservado |
| → DEVUELTO | Restaura el stock y revierte los puntos |

### Motivo de cancelación
Al cancelar o devolver podés agregar un motivo. Queda registrado en la auditoría.
`,
  },
  {
    id: "operaciones-proveedores",
    title: "Proveedores y órdenes de compra",
    section: "Operación",
    sectionIcon: "🏪",
    keywords: ["proveedor", "orden de compra", "OC", "recompra", "abastecimiento", "costo"],
    url: "/app/operaciones",
    content: `
Gestioná tus proveedores y armá órdenes de compra directamente desde el panel (sección **Operaciones**).

### Proveedores
- Cargá nombre, contacto, email y teléfono.
- Vinculá cada producto a uno o más proveedores (N:N).
- Marcá un proveedor como **primario** para las sugerencias de recompra.
- Podés guardar el **SKU del proveedor** y el **costo** por producto para calcular márgenes.

### Órdenes de compra
1. Crear → elegir proveedor → agregar ítems.
2. **Enviar**: cambia a estado "Enviada" (para tu registro).
3. **Recibir**: actualiza automáticamente el stock de los productos con \`Controlar stock\` activo.

### Sugerencias de recompra
La pestaña **Recompras** muestra todos los productos con stock ≤ stock mínimo que tienen proveedor primario asignado. Un click en "Nueva OC" arranca la compra.
`,
  },
  {
    id: "tienda-pdp",
    title: "Página de detalle de producto (PDP)",
    section: "Tienda pública",
    sectionIcon: "🌐",
    keywords: ["pdp", "detalle", "producto", "descripción larga", "galería", "imagen"],
    url: "/app/productos",
    content: `
Cada producto tiene su propia página en \`/market/tu-slug/p/nombre-producto\`.

### Qué se muestra
- Imagen, precio, disponibilidad y badge (Oferta, Popular, Nuevo).
- **Descripción larga**: si la dejás vacía, se muestra la descripción corta.
- Atributos: categoría, marca, tipo de venta.
- Selector de **variantes** (si el producto las tiene).
- Selector de **extras** con qty y precio.
- Selector de cantidad y botón "Agregar al carrito".

### Cómo agregar descripción larga
En **Productos → editar producto → campo "Descripción larga"** (el que está debajo de la descripción corta).

### PWA
Cuando alguien instala tu tienda como app en el celular, verá el nombre y logo de **tu negocio**, no el genérico de Structura.
`,
  },
  {
    id: "subdomain-tienda",
    title: "Subdominio de tu tienda",
    section: "Tienda pública",
    sectionIcon: "🌐",
    keywords: ["subdominio", "dominio", "url", "dns", "slug"],
    url: "/app/settings",
    content: `
Podés crear un subdominio propio tipo \`tutienda.structura.com.ar\`.

### Cómo crearlo
1. Ir a **Configuración → Publicar**.
2. Hacer clic en **Crear subdominio DNS**.
3. Se pre-completa con tu slug actual; podés cambiarlo por cualquier nombre válido.
4. Confirmar → el DNS se crea automáticamente.

> **Importante**: el subdominio es **independiente del slug**. Si cambiás el subdominio, el link \`/market/tu-slug\` sigue funcionando igual (no se rompen los QR existentes).
`,
  },
  {
    id: "pos-caja",
    title: "Caja del POS",
    section: "POS",
    sectionIcon: "🧾",
    keywords: ["payment_pos_mode", "cap_caja", "caja", "pos"],
    url: "/app/pos/caja",
    content: `
Abrí la sesión de caja, cobrá tickets (efectivo/QR/transferencia/tarjeta) y cerrá con arqueo. Ver también movimientos y reporte.
`,
  },
  {
    id: "pos-venta",
    title: "Vender en el POS",
    section: "POS",
    sectionIcon: "🧾",
    keywords: ["payment_pos_mode", "cap_pos", "pos"],
    url: "/app/pos",
    content: `
Usá el POS táctil para cobrar en mostrador. Escáner EAN-13, loyalty y gift cards están integrados.
`,
  },
  {
    id: "salon-mesas",
    title: "Salón y mesas",
    section: "Salón",
    sectionIcon: "🍽️",
    keywords: ["dine_in_tables", "cap_salon", "salon", "mesas"],
    url: "/app/salon",
    content: `
Gestioná mesas, sesiones y comanda. Desde cada mesa podés abrir split-check o pedido mesero.
`,
  },
  {
    id: "salon-split",
    title: "Split check",
    section: "Salón",
    sectionIcon: "🍽️",
    keywords: ["dine_in_tables", "cap_split_check", "split"],
    url: "/app/salon/split-check",
    content: `
Dividí la cuenta de una mesa. Si no hay sessionId en la URL, el sistema resuelve la sesión activa por mesa.
`,
  },
  {
    id: "salon-comanda",
    title: "Comanda mesero",
    section: "Salón",
    sectionIcon: "🍽️",
    keywords: ["dine_in_tables", "cap_comanda", "comanda"],
    url: "/app/salon/comanda",
    content: `
Tomá el pedido desde la grilla con imágenes y enviá a cocina / impresión ESC/POS.
`,
  },
  {
    id: "ingredientes-bom",
    title: "Ingredientes y BOM",
    section: "Ingredientes",
    sectionIcon: "🧪",
    keywords: ["inventory_bom", "cap_ingredientes", "cap_bom", "bom", "recetas"],
    url: "/app/ingredientes",
    content: `
Definí ingredientes y la composición (BOM) de cada producto. Al vender desde POS se deduce stock.
`,
  },
  {
    id: "compras-supply",
    title: "Compras y supply chain",
    section: "Operación",
    sectionIcon: "🚚",
    keywords: ["supply_chain", "cap_compras", "compras"],
    url: "/app/compras",
    content: `
Órdenes de compra, recepciones y lotes con alertas de vencimiento.
`,
  },
  {
    id: "produccion-cap",
    title: "Producción",
    section: "Operación",
    sectionIcon: "🏭",
    keywords: ["inventory_bom", "cap_produccion", "produccion"],
    url: "/app/produccion",
    content: `
Órdenes de producción y canales asociados al stock derivado de recetas.
`,
  },
  {
    id: "media-library",
    title: "Media Library",
    section: "Productos",
    sectionIcon: "🖼️",
    keywords: ["media", "cap_media", "media library"],
    url: "/app/media",
    content: `
Biblioteca visual multi-tenant: carpetas, tags, dedup y selector para el catálogo.
`,
  },
  {
    id: "digital-products-help",
    title: "Productos digitales",
    section: "Productos",
    sectionIcon: "💾",
    keywords: ["digital_products", "cap_digital"],
    url: "/app/digital",
    content: `
Subí archivos descargables y gestioná accesos desde Mis descargas.
`,
  },
  {
    id: "gov-payway",
    title: "Payway",
    section: "Integraciones",
    sectionIcon: "💳",
    keywords: ["payment_payway", "payway"],
    url: "/app/settings",
    content: `
Configurá Payway BYO en Pagos. Cobro tokenizado inline vía adapter.
`,
  },
  {
    id: "gov-uala",
    title: "Ualá Bis",
    section: "Integraciones",
    sectionIcon: "💳",
    keywords: ["payment_uala", "uala"],
    url: "/app/settings",
    content: `
Credenciales Ualá Bis y checkout embed/redirect.
`,
  },
  {
    id: "gov-getnet",
    title: "Getnet",
    section: "Integraciones",
    sectionIcon: "💳",
    keywords: ["payment_getnet", "getnet"],
    url: "/app/settings",
    content: `
Checkout Getnet BYO con webhook de confirmación.
`,
  },
  {
    id: "gov-modo",
    title: "MODO",
    section: "Integraciones",
    sectionIcon: "💳",
    keywords: ["payment_modo", "modo", "payment_qr"],
    url: "/app/settings",
    content: `
MODO QR/links. Refunds no soportados en adapter.
`,
  },
  {
    id: "gov-shipnow",
    title: "Shipnow",
    section: "Logística",
    sectionIcon: "🚚",
    keywords: ["shipping_shipnow", "shipnow"],
    url: "/app/settings",
    content: `
Logística Shipnow BYO y sync de estados.
`,
  },
  {
    id: "gov-treggo2",
    title: "Treggo",
    section: "Logística",
    sectionIcon: "🚚",
    keywords: ["treggo_shipping", "treggo"],
    url: "/app/settings",
    content: `
Treggo BYO + webhook HMAC.
`,
  },
  {
    id: "gov-andreani2",
    title: "Andreani",
    section: "Logística",
    sectionIcon: "🚚",
    keywords: ["andreani_shipping", "andreani"],
    url: "/app/settings",
    content: `
Andreani contratos domicilio/sucursal.
`,
  },
{
    id: "gov-cabify2",
    title: "Cabify",
    section: "Logística",
    sectionIcon: "🚚",
    keywords: ["cabify_shipping", "cabify"],
    url: "/app/settings",
    content: `
Cabify contratos domicilio/sucursal.
`,
  },
{
    id: "gov-zipnova2",
    title: "Zipnova",
    section: "Logística",
    sectionIcon: "🚚",
    keywords: ["zipnova_shipping", "zipnova"],
    url: "/app/settings",
    content: `
Zipnova contratos domicilio/sucursal.
`,
  },
  {
    id: "gov-tracking",
    title: "Tracking envíos",
    section: "Logística",
    sectionIcon: "🚚",
    keywords: ["shipping_tracking", "tracking"],
    url: "/app/pedidos",
    content: `
Seguimiento multi-carrier en el pedido.
`,
  },
  {
    id: "gov-pay-links",
    title: "Links y reembolsos",
    section: "Integraciones",
    sectionIcon: "💳",
    keywords: ["payment_links", "payment_refunds", "payment_recurring", "payment_partial", "payment_online"],
    url: "/app/settings",
    content: `
Capabilities de links, refunds, seña y pagos online.
`,
  },
  {
    id: "gov-media",
    title: "Media Library",
    section: "Productos",
    sectionIcon: "🖼️",
    keywords: ["media", "cap_media"],
    url: "/app/media",
    content: `
Biblioteca visual R2 del workspace.
`,
  },
  {
    id: "exportar-datos-csv",
    title: "Exportar datos (productos, categorías, marcas, stock)",
    section: "Operación",
    sectionIcon: "🏪",
    keywords: ["exportar", "csv", "excel", "descargar", "productos", "inventario", "categorias", "marcas"],
    url: "/app/catalogacion",
    content: `
**Qué resuelve:** te deja descargar tu catálogo, categorías, marcas y stock actual en un archivo que podés abrir en Excel o Google Sheets — útil para respaldos, análisis o compartir con contadores/proveedores.

**Cuándo conviene usarla:** antes de una migración, para armar un pedido a proveedor con el stock actual, o para llevar tu catálogo a otra herramienta.

**Requisitos previos:** ninguno.

**Dónde encontrarla:** cada listado tiene su propio botón "Exportar":
- **Catalogación → Importar/Exportar**: exporta productos, categorías y marcas desde una pantalla dedicada.
- **Productos**, **Marcas**, **Categorías**: cada listado tiene su botón de exportar individual.
- **Inventario**: exportá el stock actual o un export completo desde los botones de la pantalla.

**Configuración paso a paso:** no requiere configuración — es una acción directa.
1. Andá al listado que querés exportar (o a Catalogación → Importar/Exportar para exportar varios de una vez).
2. Hacé clic en **Exportar**.
3. El archivo \`.csv\` se descarga automáticamente con el listado completo al momento de la descarga.

**Qué debe completar el comercio:** nada — no hay campos que llenar antes de exportar.

**Qué sucede después de guardar:** no aplica (no es una configuración persistente); cada clic en Exportar genera un archivo nuevo con los datos actuales.

**Cómo verificar que quedó funcionando:** abrí el archivo descargado — cada fila corresponde a un producto/categoría/marca/ítem de stock, con sus columnas (nombre, precio, stock, etc. según el listado).

**Errores frecuentes:**
- Abrir el \`.csv\` con doble clic y que Excel interprete mal los acentos: si eso pasa, importá el archivo desde Excel eligiendo codificación UTF-8 en vez de abrirlo directo.
- Confundir "exportar" con "importar": exportar sólo descarga, no modifica nada en Structura.

**Límites o plan:** disponible desde plan Pro.

**Cómo desactivar o cambiar:** no aplica — es una acción bajo demanda, no hay nada que desactivar.

**Preguntas frecuentes:**
- *¿Puedo programar una exportación automática?* No, cada exportación es manual y bajo demanda.
- *¿El archivo incluye productos inactivos/de baja?* Depende del listado y los filtros aplicados en pantalla al momento de exportar — el export respeta los filtros activos del listado.
`,
  },
  {
    id: "api-keys-headless",
    title: "API keys — acceso headless a tu tienda",
    section: "Configuración",
    sectionIcon: "⚙️",
    keywords: ["api", "api key", "headless", "integraciones", "desarrollador", "token"],
    url: "/app/settings",
    content: `
**Qué resuelve:** te da acceso programático (vía API) a los datos de tu tienda — para conectar Structura con otro sistema propio, un ERP, o una integración a medida que tu equipo técnico necesite construir.

**Cuándo conviene usarla:** sólo si vos o tu equipo de desarrollo necesitan leer/escribir datos de Structura desde fuera del panel (por ejemplo, sincronizar stock con otro sistema). Si no tenés un equipo técnico, probablemente no la necesites.

**Requisitos previos:** conocimientos técnicos para consumir una API REST (esto es para desarrolladores, no para el uso operativo diario del comercio).

**Dónde encontrarla:** **Configuración → API**.

**Configuración paso a paso:**
1. Andá a **Configuración → API**.
2. Ponele un nombre descriptivo a la key (por ejemplo, "Integración ERP").
3. Hacé clic en **Crear key**.
4. Copiá el secreto que se muestra **en ese momento** — no se vuelve a mostrar nunca más.

**Qué debe completar el comercio:** el nombre de la key. El resto lo genera Structura.

**Qué sucede después de guardar:** la key queda activa de inmediato y puede usarse para autenticar pedidos a la API enviando el header \`X-Api-Key\` con el secreto completo (el panel sólo vuelve a mostrar el prefijo, no el secreto completo).

**Cómo verificar que quedó funcionando:** hacé una petición de prueba a la API con el header \`X-Api-Key\` — el enlace **Ver documentación de la API** dentro de la misma pantalla te lleva al detalle de endpoints disponibles.

**Errores frecuentes:**
- Perder el secreto sin copiarlo: no hay forma de recuperarlo, hay que revocar esa key y crear una nueva.
- Compartir el secreto por canales inseguros (chat, email sin cifrar) — tratalo como una contraseña.

**Límites o plan:** disponible desde plan Business.

**Cómo desactivar o cambiar:** para revocar el acceso de una key, hacé clic en el ícono de basura en la fila correspondiente — la revocación es inmediata y no se puede deshacer. No hay edición de una key existente: si necesitás cambiar algo, revocá y creá una nueva.

**Preguntas frecuentes:**
- *¿Cuántas keys puedo crear?* No hay un límite fijo documentado en el panel; creá una por cada integración distinta para poder revocarlas de forma independiente.
- *¿Qué permisos tiene una key nueva?* Se crea con permiso de lectura por defecto.
`,
  },
  {
    id: "zonas-delivery",
    title: "Zonas de delivery — cobertura propia",
    section: "Logística",
    sectionIcon: "🚚",
    keywords: ["zonas", "delivery", "cobertura", "codigo postal", "radio", "km", "envio propio"],
    url: "/app/logistica",
    content: `
**Qué resuelve:** te deja definir vos mismo hasta dónde llega tu delivery propio (sin tercerizar en un courier), ya sea por radio en kilómetros desde tu local o por zonas específicas (código postal, localidad, provincia o área dibujada en el mapa).

**Cuándo conviene usarla:** si repartís con tu propio moto/flota y necesitás que la tienda rechace pedidos fuera de tu zona real de reparto, o cobrar distinto según la distancia.

**Requisitos previos:** conocer hasta dónde llega tu reparto (radio en km, o las localidades/CP que cubrís).

**Dónde encontrarla:** **Logística → Entrega → Cobertura**.

**Configuración paso a paso:**
1. Andá a **Logística → Entrega**.
2. Elegí el método: **por radio** (definís un radio en km desde tu local, opcionalmente con tramos de precio según distancia) o **por zonas** (dibujás un polígono en el mapa o cargás código postal/localidad/provincia).
3. Activá **Zonas de delivery** para que el checkout valide contra esa cobertura.
4. Guardá la configuración de Entrega.

**Qué debe completar el comercio:** el radio en km (y tramos de precio si aplica), o las zonas/polígonos de cobertura.

**Qué sucede después de guardar:** en el checkout de la tienda pública, un cliente fuera de tu zona configurada no puede completar el pedido con entrega a domicilio — ve un aviso de que su dirección está fuera de cobertura.

**Cómo verificar que quedó funcionando:** hacé un pedido de prueba en tu tienda pública con una dirección que sabés que está fuera de tu zona, y confirmá que el checkout la rechaza; probá también con una dirección dentro de la zona para confirmar que sí la acepta.

**Errores frecuentes:**
- Definir un radio en km sin considerar que la distancia se mide en línea recta, no por calles — en zonas con ríos, autopistas o barreras geográficas el radio real de reparto puede ser menor.
- Dejar "Zonas de delivery" desactivado después de configurar zonas — sin activar el toggle, la cobertura queda cargada pero no se aplica en el checkout.

**Límites o plan:** disponible desde plan Pro.

**Cómo desactivar o cambiar:** desactivá el toggle de **Zonas de delivery** para volver a aceptar pedidos sin restricción de cobertura, o editá el radio/zonas en cualquier momento desde la misma pantalla.

**Preguntas frecuentes:**
- *¿Puedo combinar radio y zonas dibujadas?* La configuración de cobertura es por un método a la vez (radio o zonas); elegí el que mejor represente tu reparto real.
- *¿Esto reemplaza a un courier tercerizado (Andreani, OCA, etc.)?* No, es para reparto propio. Los couriers tercerizados se configuran como integraciones de envío aparte.
`,
  },
  {
    id: "tours-in-app",
    title: "Tours in-app — guías de onboarding",
    section: "Configuración",
    sectionIcon: "⚙️",
    keywords: ["tours", "onboarding", "guia", "ayuda contextual", "product_tours"],
    url: "/app/settings",
    content: `
**Qué resuelve:** son los recorridos guiados con recuadros que aparecen la primera vez que entrás a una pantalla nueva (Pedidos, Fidelización, Cupones, etc.), señalando qué hace cada botón. Sirven para que un usuario nuevo del equipo se oriente sin tener que preguntar.

**Cuándo conviene usarla:** dejala activada mientras el equipo se está familiarizando con el panel; podés desactivarla una vez que el equipo ya conoce Structura y prefiere que no aparezcan.

**Requisitos previos:** ninguno.

**Dónde encontrarla:** los tours aparecen automáticamente al entrar a cada pantalla la primera vez. El interruptor general está en tu plan/feature — consultá con soporte si no ves el CTA "Ver explicación de nuevo" en las pantallas que ya tienen guía.

**Configuración paso a paso:** no requiere configuración manual — se activan solos la primera vez que un usuario visita una pantalla con tour disponible.
1. Entrá a una pantalla con tour disponible (por ejemplo, Pedidos o Fidelización).
2. El recorrido guiado aparece automáticamente, resaltando los elementos clave con una explicación.
3. Podés avanzar paso a paso o cerrarlo en cualquier momento.

**Qué debe completar el comercio:** nada.

**Qué sucede después de guardar:** no aplica — no es una configuración que se guarda, es un estado por pantalla y por usuario (una vez visto, no se repite salvo que lo reinicies).

**Cómo verificar que quedó funcionando:** si ya viste el tour de una pantalla, buscá el botón/enlace **"Ver explicación de nuevo"** — al hacer clic, el tour se reinicia y vuelve a mostrarse.

**Errores frecuentes:**
- Pensar que el tour "desapareció" por un error: en realidad, cada tour se muestra una sola vez por pantalla y usuario; para volver a verlo hay que usar "Ver explicación de nuevo".

**Límites o plan:** disponible desde plan Starter (todos los planes).

**Cómo desactivar o cambiar:** si tu workspace tiene la feature de tours desactivada a nivel plan/tenant, ningún tour se muestra en ninguna pantalla, sin excepción — consultá con soporte para desactivarla a nivel cuenta si el equipo ya no la necesita.

**Preguntas frecuentes:**
- *¿Los tours son distintos para cada usuario del equipo?* Sí, el estado de "ya visto" es por usuario, no por workspace — un usuario nuevo en el equipo va a ver los tours aunque el resto del equipo ya los haya visto.
- *¿Hay tours en todas las pantallas?* No, sólo en las pantallas donde el equipo de producto definió que aporta valor real — no todas las pantallas tienen tour.
`,
  },
  {
    id: "soporte-in-app",
    title: "Soporte in-app — centro de ayuda y tickets",
    section: "Configuración",
    sectionIcon: "⚙️",
    keywords: ["soporte", "tickets", "ayuda", "contacto", "consulta"],
    url: "/app/settings",
    content: `
**Qué resuelve:** te permite abrir una consulta o reportar un problema directamente desde el panel, con seguimiento de estado (abierto, respondido, cerrado) y adjuntos, sin salir de Structura ni escribir por otro canal.

**Cuándo conviene usarla:** cuando tenés una duda puntual, un problema técnico, o necesitás pedir algo al equipo de Structura y querés dejar registro escrito con historial.

**Requisitos previos:** ninguno.

**Dónde encontrarla:** **Configuración → Soporte**.

**Configuración paso a paso:**
1. Andá a **Configuración → Soporte**.
2. Hacé clic en crear un ticket nuevo.
3. Completá el **asunto** y describí tu consulta en el cuerpo del mensaje.
4. Adjuntá una captura o archivo si ayuda a explicar el problema.
5. Enviá el ticket.

**Qué debe completar el comercio:** asunto, descripción del problema o consulta, y opcionalmente un adjunto.

**Qué sucede después de guardar:** el ticket queda con estado abierto y prioridad asignada; el equipo de soporte responde dentro del panel, y vas a ver la conversación como un chat con historial de mensajes.

**Cómo verificar que quedó funcionando:** volvé a **Configuración → Soporte** y confirmá que tu ticket aparece en la lista con su estado actual; un contador de mensajes no leídos te avisa cuando hay una respuesta nueva.

**Errores frecuentes:**
- Abrir un ticket nuevo por cada mensaje de una misma consulta en vez de responder dentro del ticket ya abierto — esto dificulta el seguimiento.
- Adjuntar archivos muy pesados: si falla la carga, probá con una imagen comprimida o un extracto en vez del archivo completo.

**Límites o plan:** disponible desde plan Starter (todos los planes).

**Cómo desactivar o cambiar:** no hay forma de desactivar el acceso a soporte — es parte de la operación base de cualquier workspace.

**Preguntas frecuentes:**
- *¿Cuánto tarda la respuesta?* Varía según la prioridad y volumen de consultas; no hay un SLA fijo documentado en el panel.
- *¿Puedo reabrir un ticket cerrado?* Si tenés una consulta relacionada a algo ya resuelto, es mejor abrir un ticket nuevo referenciando el anterior.
`,
  },
];

const PRINTING_HELP_ARTICLES: HelpArticle[] = [
  {
    id: "setup-thermal-printers",
    section: "Impresoras",
    sectionIcon: "🖨️",
    keywords: [],
    title: "Configurar impresoras térmicas",
    summary: "Cómo conectar y configurar impresoras para imprimir tickets y comandas",
    content: `
## Configurar impresoras térmicas

Las impresoras térmicas imprimen directamente desde tu navegador usando **QZ Tray** — sin ventanas de impresión, sin PDF, de forma instantánea.

### Paso 1 — Instalar QZ Tray

QZ Tray es una app gratuita que instalás una única vez en cada dispositivo:

1. Ir a **Configuración → Impresoras**
2. Click en **Descargar QZ Tray**
3. Instalar y ejecutar

### Paso 2 — Conectar

Una vez instalado:
1. Abrí Configuración → Impresoras
2. Click en **Ya lo tengo instalado**
3. El estado cambia a ✅ Conectado

### Paso 3 — Detectar impresoras

1. Click en **Buscar impresoras**
2. Aparece la lista de impresoras disponibles en este dispositivo

### Paso 4 — Asignar roles

Asigná cada impresora a su rol:
- **Caja principal**: tickets de venta y recibos
- **Cocina**: comandas de cocina
- **Barra**: comandas de barra

### Paso 5 — Test de impresión

Usá el botón **Imprimir prueba** para verificar que todo funciona.

### ⚠️ Importante

- La configuración se guarda en **este dispositivo**. Si usás otra computadora, repetí los pasos.
- QZ Tray debe estar corriendo (en la bandeja del sistema) cada vez que querés imprimir.
`,
  },
  {
    id: "install-qz-tray",
    section: "Impresoras",
    sectionIcon: "🖨️",
    keywords: [],
    title: "Instalar QZ Tray",
    summary: "QZ Tray conecta el navegador con tu impresora local",
    content: `
## ¿Qué es QZ Tray?

QZ Tray es una aplicación gratuita de código abierto que corre en segundo plano en tu computadora. Actúa como puente entre el navegador y la impresora física.

### ¿Por qué QZ Tray?

- Imprime **sin ventanas del navegador**
- Usa comandos **ESC/POS** nativos de impresoras térmicas
- Compatible con **Epson, XPrinter, Star, Bixolon** y cualquier impresora ESC/POS
- Funciona en **Windows, macOS y Linux**

### Instalación

1. Descargar desde: [qz.io/download](https://qz.io/download/)
2. Instalar el archivo .exe (Windows) o .dmg (macOS)
3. Al ejecutarlo aparece en la bandeja del sistema (🖨️)
4. Abrí Structura → Configuración → Impresoras → "Ya lo tengo instalado"

### QZ Tray en la bandeja del sistema

Buscalo en:
- **Windows**: esquina inferior derecha (bandeja)
- **macOS**: barra superior (menú bar)

Debe estar corriendo siempre que uses la función de impresión.

### ¿Qué pasa si se cierra?

Si cerrás QZ Tray, la impresión automática deja de funcionar. Simplemente volvé a abrirlo desde el menú de inicio o la carpeta de aplicaciones.
`,
  },
  {
    id: "printing-troubleshooting",
    section: "Impresoras",
    sectionIcon: "🖨️",
    keywords: [],
    title: "Problemas comunes de impresión",
    summary: "Qué hacer si la impresora no responde",
    content: `
## Problemas comunes de impresión

### "QZ Tray no encontrado"

**Causa**: QZ Tray no está instalado o no está corriendo.

**Solución**:
1. Verificar que QZ Tray esté instalado
2. Buscarlo en la bandeja del sistema o menú de inicio
3. Abrirlo si está cerrado
4. Hacer click en "Ya lo tengo instalado" en Configuración → Impresoras

---

### "No se detectaron impresoras"

**Causa**: La impresora no está encendida o no tiene driver.

**Solución**:
1. Encender la impresora
2. Verificar que Windows/macOS la reconoce (Panel de control → Dispositivos e impresoras)
3. Instalar el driver del fabricante si es necesario
4. Volver a hacer click en "Buscar impresoras"

---

### "El ticket sale en blanco"

**Causa**: El papel térmico está al revés.

**Solución**: Girá el rollo de papel. El lado térmico (brillante) va hacia la cabeza de impresión.

---

### "El texto sale cortado o ilegible"

**Causa**: La configuración de ancho de papel no coincide con el papel real.

**Solución**:
1. Ir a Configuración → Impresoras
2. Expandir el rol de la impresora
3. Seleccionar el ancho correcto: **58mm** o **80mm**
4. Guardar y hacer test de impresión

---

### "La impresora no corta el papel"

**Causa**: La opción "Corte automático" está desactivada.

**Solución**: Activar "Corte automático" en la configuración de la impresora.
`,
  },
  {
    id: "escpos-vs-pdf",
    section: "Impresoras",
    sectionIcon: "🖨️",
    keywords: [],
    title: "Diferencia entre impresión ESC/POS y PDF",
    summary: "Por qué Structura usa ESC/POS en lugar de PDF",
    content: `
## ESC/POS vs PDF

### ¿Qué es ESC/POS?

ESC/POS es el lenguaje nativo de las impresoras térmicas de punto de venta.
Es un protocolo de comandos en bytes que le indica directamente a la impresora qué imprimir.

### Comparación

| Característica | ESC/POS (Structura) | PDF / window.print() |
|---|---|---|
| Velocidad | Instantánea | 3-5 segundos |
| Ventana del navegador | ❌ No aparece | ✅ Aparece siempre |
| Corte automático | ✅ Sí | ❌ No |
| Formato para térmica | ✅ Nativo | ⚠️ Requiere ajuste manual |
| Impresión silenciosa | ✅ Sin intervención | ❌ Requiere clic en "Imprimir" |

### ¿Por qué importa esto?

En un punto de venta real, el mozo o la cajera **no puede estar haciendo click en "Imprimir"** cada vez que sale un pedido. La impresión debe ser:

- **Instantánea**: al confirmar el pedido
- **Silenciosa**: sin ventanas ni diálogos
- **Confiable**: siempre el mismo formato

ESC/POS con QZ Tray hace exactamente eso.
`,
  },
  {
    id: "printer-network-setup",
    section: "Impresoras",
    sectionIcon: "🖨️",
    keywords: [],
    title: "Conectar impresora térmica por red (Ethernet)",
    summary: "La forma más estable y profesional de conectar una impresora",
    content: `
## Conectar impresora térmica por red

Conectar la impresora por **cable de red (Ethernet)** al router es la forma más estable y recomendada para un local. Funciona desde cualquier dispositivo de la red, sin depender de un cable USB ni de drivers.

### Paso 1 — Conectar el cable

Enchufá un cable de red desde la impresora al router o switch.

### Paso 2 — Imprimir el self-test

Cada impresora tiene un auto-test que muestra su configuración de red:

1. Apagá la impresora
2. Mantené apretado el botón **FEED** (avance de papel)
3. Encendela sin soltar el botón
4. Soltá cuando empiece a imprimir

El ticket muestra: **IP address**, **Subnet Mask**, **Gateway** y si **DHCP** está activado.

### Paso 3 — Verificar que esté en tu red

La impresora debe estar en la **misma red** que tus dispositivos.

Ejemplo: si tu red es **192.168.0.x**, la impresora debe tener una IP como **192.168.0.100** y el gateway **192.168.0.1**.

> **Problema común**: muchas impresoras vienen con IP fija de fábrica en otra red (ej: 192.168.123.100 con DHCP deshabilitado). En ese caso hay que cambiarle la IP.

### Paso 4 — Cambiar la IP (si está en otra red)

1. Agregá temporalmente una IP de la red de la impresora a tu computadora:
   - Mac: \`sudo ifconfig en0 alias 192.168.123.50 255.255.255.0\`
2. Abrí en el navegador la IP de la impresora (ej: \`http://192.168.123.100\`)
3. Entrá a **Configuration** → cambiá:
   - IP address → una libre de tu red (ej: 192.168.0.100)
   - Gateway → el de tu router (ej: 192.168.0.1)
   - Activá **DHCP** si querés que tome IP automática
4. Guardá y reiniciá la impresora
5. Quitá la IP temporal: \`sudo ifconfig en0 -alias 192.168.123.50\`

### Paso 5 — Verificar conexión

Probá el puerto de impresión (9100):
\`\`\`
nc -z -v 192.168.0.100 9100
\`\`\`
Si dice **"succeeded"**, está lista.

### Paso 6 — Configurar en Structura

En **Configuración → Impresoras**, en el campo de nombre escribí la **IP** (ej: \`192.168.0.100\`). Structura detecta que es una dirección de red y se conecta directo por TCP — sin pasar por el sistema operativo.

> **Ventaja**: con impresora por red no necesitás instalarla en macOS/Windows ni configurar drivers. QZ Tray le manda los comandos ESC/POS directo a la IP.
`,
  },
  {
    id: "printer-trust-prompt",
    section: "Impresoras",
    sectionIcon: "🖨️",
    keywords: [],
    title: "Eliminar el popup de confianza de QZ Tray",
    summary: "Por qué aparece y cómo desactivarlo definitivamente",
    content: `
## El popup "Untrusted website" de QZ Tray

Cuando imprimís, QZ Tray puede mostrar un popup **"Action Required — An anonymous request wants to print"** con botones Allow / Block.

### ¿Por qué aparece?

Es una medida de seguridad de QZ Tray: por defecto pregunta antes de dejar que un sitio web imprima, para evitar que cualquier página imprima sin permiso.

### Solución rápida (temporal)

1. Marcá la casilla **"Remember this decision"**
2. Click en **"Allow"**

Esto lo recuerda para esta máquina. Puede resetearse si reiniciás QZ Tray.

### Solución definitiva (Structura ya la incluye)

Structura firma cada pedido de impresión con un **certificado digital propio**. Una vez que QZ Tray confía en ese certificado, **no vuelve a preguntar nunca más** en esa PC — ni al reiniciar QZ Tray ni al recargar la página.

Pero QZ Tray **no confía en ningún certificado autofirmado por default**: en una PC nueva, la primera conexión siempre muestra este aviso una vez, sin importar qué tan bien esté configurada la firma del lado del servidor. Hay dos formas de resolverlo:

1. **Rápida (por PC)**: tildar "Remember this decision" y click en "Allow" la primera vez. Queda guardado para esa máquina.
2. **Definitiva (sin aviso ni la primera vez)**: descargar el certificado de Structura desde **Ajustes → Impresoras** ("Descargar certificado (override.crt)") y copiarlo en la carpeta de instalación de QZ Tray (\`C:\\Program Files\\QZ Tray\\override.crt\` en Windows), luego reiniciar QZ Tray. Con el certificado pre-instalado, QZ Tray confía en Structura desde la primera conexión.

Si el popup dice **"Invalid Signature"** en vez de "Untrusted website", ahí sí puede ser un problema real de configuración — contactá a soporte para verificar que la clave privada del servidor esté configurada.

> **Seguridad**: la firma solo autoriza **imprimir** desde Structura. No da acceso a ningún dato. La clave privada vive solo en el servidor, nunca en el navegador.
`,
  },
  {
    id: "print-types",
    section: "Impresoras",
    sectionIcon: "🖨️",
    keywords: [],
    title: "Tipos de impresión: ticket, comanda y ticket de cambio",
    summary: "Qué imprime cada botón según tu tipo de negocio",
    content: `
## Tipos de impresión

Desde el detalle de cada pedido podés imprimir distintos comprobantes. Los botones que ves dependen del **tipo de negocio** configurado.

### 🧾 Ticket (todos los rubros)

El comprobante de venta para el cliente. Incluye:
- **N° de orden** (número incremental, fácil de cantar en mostrador)
- Fecha y hora
- Items **agrupados por categoría**
- Adicionales de cada producto con su precio
- **Medio de pago** (Efectivo, Tarjeta de Crédito/Débito, Transferencia, etc.)
- **Estado del pago** (Pagado / Pendiente / Rechazado)
- Total

### 🍔 Comanda (solo gastronomía)

Para cocina o barra — disponible en **Fast food** y **Restaurante**. Incluye:
- **N° de orden GRANDE** en un recuadro al centro, visible desde lejos
- Items agrupados por categoría (entradas, principales, bebidas…)
- **Adicionales bajo cada producto** (cheddar extra, sin cebolla, etc.) — lo que cocina necesita preparar
- No muestra precios (no hace falta en cocina)

### 🔁 Ticket de cambio (indumentaria / bazar)

Para devoluciones y cambios — disponible en **Indumentaria** y **Bazar**. Incluye:
- N° de orden
- Items **sin precios**
- Leyenda "Comprobante sin valor fiscal — Válido para cambio"

> El **N° de orden** es incremental por negocio (1, 2, 3…) y reemplaza al ID largo y aleatorio. Es mucho más práctico para coordinar pedidos en el mostrador y la cocina.
`,
  },
  {
    id: "share-ticket-whatsapp",
    section: "Impresoras",
    sectionIcon: "🖨️",
    keywords: [],
    title: "Compartir el ticket por WhatsApp",
    summary: "Copiar el ticket y enviarlo sin imprimir ni descargar",
    content: `
## Compartir el ticket por WhatsApp

No siempre necesitás imprimir. Podés enviarle el ticket al cliente por WhatsApp en segundos.

### Cómo hacerlo

1. Abrí el pedido y tocá **Ticket**
2. En el diálogo, tocá el botón **Copiar**
3. El ticket se copia al portapapeles en formato texto (con el detalle, total y medio de pago)
4. Abrí el chat de WhatsApp del cliente y **pegá** (Cmd/Ctrl+V o mantener presionado → Pegar)
5. Enviá

### Ventajas

- No necesitás descargar una imagen ni un PDF
- El texto se ve bien en cualquier teléfono
- Incluye el N° de orden, los items por categoría, adicionales y el total

> El texto copiado usa formato de WhatsApp (negritas con asteriscos), así que el nombre del negocio y el total se ven destacados.
`,
  },
];

const GASTRONOMIA_HELP_ARTICLES: HelpArticle[] = [
  {
    id: "pos-open-session",
    title: "Abrir una sesión de caja (POS)",
    section: "POS",
    sectionIcon: "🏧",
    keywords: ["pos", "caja", "sesión", "apertura", "mostrador", "venta directa"],
    url: "/app/pos/caja",
    content: `
El módulo **POS** te permite vender en mostrador con caja registradora.

**Para abrir la sesión:**
1. Andá a **POS → Caja**
2. Ingresá el monto inicial en efectivo
3. Hacé clic en **Abrir sesión**

A partir de ahí, cada venta se registra en la sesión y el arqueo final la contempla.

> **Requisito:** el plan Business debe tener el feature **POS Mode** habilitado.
`,
  },
  {
    id: "pos-sell",
    title: "Vender en el mostrador",
    section: "POS",
    sectionIcon: "🏧",
    keywords: ["pos", "venta", "mostrador", "carrito", "código de barras", "cobrar"],
    url: "/app/pos/caja",
    content: `
Con la sesión abierta:

1. Buscá el producto por nombre o escaneá el código de barras
2. Ajustá la cantidad si es necesario
3. Elegí el medio de pago: **Efectivo · Transferencia · Tarjeta**
4. Confirmá la venta

El stock se descuenta automáticamente. Si el producto tiene receta (BOM), también se descuentan los ingredientes.
`,
  },
  {
    id: "pos-close-session",
    title: "Cerrar caja y hacer el arqueo",
    section: "POS",
    sectionIcon: "🏧",
    keywords: ["pos", "cierre", "arqueo", "diferencia", "reporte", "caja"],
    url: "/app/pos/caja",
    content: `
Al final del turno:

1. Andá a **POS → Caja → Cerrar sesión**
2. Contá el efectivo real en caja e ingresalo
3. El sistema muestra la **diferencia** vs lo esperado
4. Confirmá el cierre

El reporte queda guardado en **POS → Reporte** con historial de sesiones.
`,
  },
  {
    id: "salon-tables",
    title: "Gestión de mesas (Dine-In)",
    section: "Salón",
    sectionIcon: "🍽️",
    keywords: ["salon", "mesas", "dine-in", "comanda", "sector", "terraza", "mozo"],
    url: "/app/salon",
    content: `
El módulo **Salón** muestra el mapa de mesas en tiempo real.

**Estados de cada mesa:**
- 🟢 **Libre** — disponible
- 🟡 **Pidiendo** — el cliente está por terminar el pedido
- 🟠 **Ocupada** — comanda abierta
- 🔴 **Pago pendiente** — esperando cobro

Hacé clic en una mesa para ver su comanda, agregar ítems o iniciar el cobro.
`,
  },
  {
    id: "salon-split-check",
    title: "Dividir la cuenta entre comensales",
    section: "Salón",
    sectionIcon: "🍽️",
    keywords: ["division", "cuenta", "comensales", "split", "partes", "separar"],
    url: "/app/salon/split-check",
    content: `
Desde la comanda de una mesa, tocá **Dividir cuenta**:

- **Partes iguales:** elegís cuántos y el sistema divide el total
- **Por ítem:** cada comensal elige qué consumió

Cada parte genera su propio cobro. Podés mezclar medios de pago entre comensales.
`,
  },
  {
    id: "salon-qr",
    title: "QR de mesa — pedido sin mozo",
    section: "Salón",
    sectionIcon: "🍽️",
    keywords: [
      "qr",
      "mesa",
      "autoservicio",
      "sin mozo",
      "escanear",
      "storefront",
      "dine_in_tables",
      "cap_qr_ordering",
    ],
    url: "/app/salon",
    content: `
Cada mesa tiene un **código QR único**. Los clientes lo escanean con el celular y hacen el pedido directamente.

El pedido llega a la comanda de la mesa en tiempo real — sin que el mozo tome el pedido manualmente.

Descargá los QR desde **Salón → Configurar sectores**.
`,
  },
  {
    id: "bom-ingredients",
    title: "Cargar ingredientes e insumos (BOM)",
    section: "Ingredientes",
    sectionIcon: "🧪",
    keywords: ["ingrediente", "insumo", "bom", "stock", "receta", "escandallo"],
    url: "/app/ingredientes",
    content: `
En **Ingredientes** cargás todos los insumos que usás en tus recetas.

Cada ingrediente tiene:
- **Nombre** y **unidad** (kg, g, L, mL, unidades)
- **Stock actual** y **stock mínimo** (para alertas)
- **Proveedor principal** (para OC automáticas)

El stock se actualiza automáticamente con cada venta que tenga receta asignada.
`,
  },
  {
    id: "bom-recipes",
    title: "Asignar receta a un producto",
    section: "Ingredientes",
    sectionIcon: "🧪",
    keywords: ["receta", "composicion", "escandallo", "ingrediente", "producto", "bom"],
    url: "/app/ingredientes",
    content: `
Para asignar la receta de un plato:

1. Andá a **Productos → (el producto) → Composición**
2. Agregá ingredientes con cantidad y unidad
3. Guardá

Desde ese momento, cada venta de ese producto descuenta los ingredientes de la receta.

> Podés usar sub-recetas: si la salsa bolognesa es un ingrediente en sí misma, definila como receta aparte y usala en la lasaña.
`,
  },
  {
    id: "supply-suppliers",
    title: "Gestionar proveedores",
    section: "Compras",
    sectionIcon: "🚚",
    keywords: ["proveedor", "compras", "abastecimiento", "supply chain", "cuit", "contacto"],
    url: "/app/compras",
    content: `
En **Compras → Proveedores** registrás los datos de cada proveedor:

- Razón social, CUIT, email y teléfono
- Condición de pago (contado, 30 días, etc.)
- Catálogo de ítems con precio acordado

Cada ingrediente puede tener un proveedor principal asignado para generar OC en un clic.
`,
  },
  {
    id: "supply-purchase-orders",
    title: "Crear y gestionar órdenes de compra",
    section: "Compras",
    sectionIcon: "🚚",
    keywords: ["orden de compra", "oc", "compra", "proveedor", "pedido", "recepcion"],
    url: "/app/compras/ordenes",
    content: `
**Para crear una OC:**
1. Andá a **Compras → Órdenes → Nueva OC**
2. Elegí el proveedor
3. Agregá los ítems con cantidad
4. Confirmá → el proveedor recibe un email

**Estados de una OC:**
- **Borrador** → **Enviada** → **Confirmada** → **Recibida** → **Cerrada**

Al registrar la recepción, el stock de ingredientes se acredita automáticamente.
`,
  },
  {
    id: "supply-batches",
    title: "Lotes y vencimientos",
    section: "Compras",
    sectionIcon: "🚚",
    keywords: ["lote", "vencimiento", "fefo", "batch", "fecha", "cuarentena", "alerta"],
    url: "/app/compras/lotes",
    content: `
Cada recepción de mercadería genera un **lote** con fecha de vencimiento.

Se usa primero lo que vence antes, para no perder mercadería.

**Estados de un lote:**
- 🟢 **Activo** — en uso
- 🔴 **Vencido** — requiere acción
- ⚠️ **En revisión** — bloqueado por un problema de calidad
- ⚪ **Agotado** — consumido completamente

Activá las **alertas de vencimiento** en Configuración para recibir avisos con N días de anticipación.
`,
  },

  {
    id: "ordenes-hub",
    title: "Órdenes y pedidos",
    section: "Pedidos",
    sectionIcon: "🛒",
    keywords: ["ordenes", "pedidos", "cola"],
    url: "/app/ordenes",
    content: `
Desde **Órdenes** gestionás la cola de pedidos del día: confirmar, preparar, cobrar y entregar.

El mismo flujo histórico de **Pedidos** vive acá como hub operativo.
`,
  },
  {
    id: "clientes-hub",
    title: "Clientes",
    section: "Operación",
    sectionIcon: "⚙️",
    keywords: ["clientes", "crm", "email"],
    url: "/app/clientes",
    content: `
En **Clientes** ves compradores por email, historial de pedidos y datos de contacto.

Usalo junto a **Métricas → RFM** para detectar activos, en riesgo e hibernando.
`,
  },
  {
    id: "eventos-hub",
    title: "Eventos y entradas",
    section: "Operación",
    sectionIcon: "⚙️",
    keywords: ["eventos", "tickets", "qr"],
    url: "/app/eventos",
    content: `
Creá eventos, vendé entradas y validá QR desde el panel.

Ideal para ferias, lanzamientos o noches especiales del local.
`,
  },
  {
    id: "descuentos-hub",
    title: "Ventas",
    section: "Operación",
    sectionIcon: "⚙️",
    keywords: ["descuentos", "cupones", "promos", "ofertas", "ventas", "combos", "packs"],
    url: "/app/descuentos",
    content: `
Configurá cupones, promociones, combos y packs desde **Ventas**.

Combiná con fidelización para reactivar clientes en riesgo.
`,
  },
  {
    id: "facturacion-hub",
    title: "Facturación electrónica",
    section: "Configuración",
    sectionIcon: "⚙️",
    keywords: ["facturacion", "afip", "tusfacturas"],
    url: "/app/facturacion",
    content: `
Si tu plan incluye facturación, conectá el proveedor y emití comprobantes desde los pedidos.
`,
  },
  {
    id: "reservas-hub",
    title: "Reservas de mesa",
    section: "Operación",
    sectionIcon: "⚙️",
    keywords: ["reservas", "mesas", "salon"],
    url: "/app/reservas",
    content: `
Gestioná reservas de salón: horarios, capacidad y confirmaciones.

Disponible en perfiles gastronómicos con el módulo activo.
`,
  },
  {
    id: "experience-hub",
    title: "Experiencia de tienda",
    section: "Tienda pública",
    sectionIcon: "🛍️",
    keywords: ["experience", "builder", "publicar", "storefront"],
    url: "/app/experience",
    content: `
Diseñá y **publicá** la experiencia de tu tienda (home, catálogo, branding).

Sin publicar, el checklist de activación no marca la tienda como lista.
`,
  },
  {
    id: "hardware-hub",
    title: "Hardware",
    section: "Configuración",
    sectionIcon: "⚙️",
    keywords: ["hardware", "impresora", "balanza", "pos"],
    url: "/app/hardware",
    content: `
Conectá impresoras, balanzas y dispositivos del local cuando el flag Hardware esté activo.
`,
  },
  {
    id: "developer-hub",
    title: "Developer / API Docs",
    section: "Integraciones",
    sectionIcon: "🔌",
    keywords: ["api", "developer", "webhooks", "openapi"],
    url: "/app/api-docs",
    content: `
Consultá la documentación OpenAPI del tenant, webhooks y ejemplos de integración.

Ideal para conectar tu ERP, app propia o automatizaciones.
`,
  },
  {
    id: "kyc-didit-guide",
    title: "Verificación de identidad (KYC) con Didit",
    section: "Legal y KYC",
    sectionIcon: "🪪",
    keywords: ["kyc", "didit", "verificación", "identidad", "webhook", "legal"],
    url: "/app/cuenta",
    content: `
## 1. Qué es KYC en Structura

KYC (*Know Your Customer*) es la verificación de identidad del **Owner** del Tenant. Confirma
que la persona detrás de un comercio es quien dice ser, usando el proveedor externo **Didit**
(Hosted Session).

## 2. Por qué es opcional en esta versión

El MVP actual **nunca bloquea** ventas, pedidos, storefront ni ninguna operación del Tenant por
no tener KYC aprobado. Es un paso opcional del checklist "Configurá tu tienda" y un banner
informativo en el Home — nada más. Una versión futura podría atarlo a habilitar funcionalidades
puntuales de cobros, pero eso no está implementado todavía.

## 3. Quién puede iniciarlo

Solo el **Owner** del Tenant (o super_admin de plataforma). La autorización se valida
server-side en cada request — nunca confía en lo que declare el frontend.

## 4. Configurar las credenciales de Didit

Variables de entorno (nunca en el código, nunca en el frontend):

- \`DIDIT_API_KEY\` — API key de Didit (header \`x-api-key\` en cada request al backend de Didit).
- \`DIDIT_WORKFLOW_ID\` — UUID del workflow de verificación elegido en el panel de Didit
  (Workflows → copiar ID completo, no el truncado que se ve en la tabla).
- \`KYC_WEBHOOK_SECRET\` — el "Webhook Secret Key" que Didit muestra **una sola vez** al crear
  el destino del webhook en *Business Console → API & Webhooks*. Si se pierde, hay que borrar
  el destino y crearlo de nuevo — Didit no lo vuelve a mostrar.

En local van en \`.env\` (nunca se commitea). En producción, como variables de entorno del sitio
en Netlify.

## 5. Configurar el workflow

En el panel de Didit (*Workflows*), cada workflow define qué se verifica (documento, biometría,
AML, etc.) y tiene su propio costo por verificación. El \`workflow_id\` se copia desde ahí y se
carga en \`DIDIT_WORKFLOW_ID\` — nunca se hardcodea en el código.

## 6. Cómo se crea una sesión

El Owner aprieta "Verificar identidad" en **Mi Cuenta**. El frontend llama a
\`POST /api/kyc/session\` (requiere ser Owner y que el workspace tenga habilitado el switch de
Legal+KYC). El backend llama a Didit (\`POST /v3/session/\`) pasando \`workflow_id\`,
\`vendor_data\` (el \`tenantId\`, para poder resolver el tenant en el webhook) y una URL de
\`callback\` para volver a Mi Cuenta al terminar.

## 7. Cómo se abre la experiencia de verificación

El backend devuelve una \`url\` de Didit. El frontend redirige la página completa a esa URL
(no un popup) — los popups suelen bloquear el acceso a la cámara en navegadores móviles, y la
mayoría de los workflows piden foto de documento y/o selfie.

## 8. Cómo funciona el webhook

Didit notifica cambios de estado a \`POST /api/kyc/webhook\`. Cada request trae:

- \`X-Signature\` — firma HMAC-SHA256 sobre el body crudo.
- \`X-Timestamp\` — epoch en segundos del envío.
- Body JSON con \`event_id\`, \`session_id\`, \`status\`, \`vendor_data\` (el tenantId) y,
  cuando corresponde, \`decision\`.

## 9. Cómo se valida la firma

Se recalcula HMAC-SHA256 del body **crudo** (antes de parsear JSON) con \`KYC_WEBHOOK_SECRET\`
y se compara contra \`X-Signature\` con \`timingSafeEqual\` (comparación a tiempo constante, evita
timing attacks). Si no hay secreto configurado, el webhook rechaza **todo** — nunca procesa sin
poder verificar la firma. También se rechaza si \`X-Timestamp\` está a más de 300 segundos del
reloj del servidor (anti-replay).

## 10. Cómo se persiste el resultado

En \`sm_kyc_records\` (una fila por tenant/provider): \`status\`, \`provider_session_id\`,
\`rejection_code\`, \`approved_at\`. La idempotencia por \`event_id\` vive en
\`sm_kyc_webhook_events\` — un \`event_id\` repetido (reintento de Didit) no reprocesa nada.

## 11. Qué datos se guardan y cuáles nunca

**Se guarda:** tenant, provider, session id, status, código de rechazo, timestamps.

**Nunca se guarda:** DNI/pasaporte, imágenes del documento, selfie, biometría, ni el payload
completo que manda Didit — solo se extraen los campos de status.

## 12. Qué pasa ante cada resultado

- **Aprobado** → \`approved\`, se guarda \`approved_at\`. El banner del Home desaparece.
- **Rechazado** → \`rejected\` + \`rejection_code\` si Didit lo informó. El Owner puede reintentar.
- **Expirado / abandonado** → \`expired\`. Se puede iniciar una verificación nueva.
- **Error de configuración** (falta \`DIDIT_API_KEY\`/\`DIDIT_WORKFLOW_ID\`) → \`503\`, sin crear
  sesión.

## 13. Cómo probar el flujo en sandbox

Didit permite simular resultados deterministas con \`sandbox_scenario\` en modo sandbox. Para el
flujo real end-to-end (Owner → Mi Cuenta → Didit → webhook → estado actualizado) conviene
probarlo desde un celular real o el simulador iOS/Android, porque la mayoría de los workflows
piden cámara.

## 14. Cómo diagnosticar una sesión que quedó pendiente

1. Revisar **Admin → Legal → KYC** (\`/admin/ops/legal/kyc\`): filtrar por tenant o status.
2. Si Didit no mandó webhook, se puede consultar el fallback
   \`GET /v3/session/{session_id}/decision/\` (implementado en \`getDiditSessionDecision\`).
3. Si sigue sin resolverse, un super_admin puede hacer un **override manual** (aprobar/rechazar)
   desde esa misma pantalla — queda auditado en \`sm_audit_logs\` como \`KYC_ADMIN_OVERRIDE\`.

## 15. Cómo rotar/revocar la API key

Se regenera desde el panel de Didit y se actualiza \`DIDIT_API_KEY\` en las variables de entorno
(local y Netlify). No requiere cambios de código. El \`KYC_WEBHOOK_SECRET\` es independiente —
rotarlo implica recrear el destino del webhook en Didit.

## 16. Variables de entorno requeridas por ambiente

| Variable | Local (\`.env\`) | Producción (Netlify) |
|---|---|---|
| \`DIDIT_API_KEY\` | sí | sí |
| \`DIDIT_WORKFLOW_ID\` | sí | sí |
| \`KYC_WEBHOOK_SECRET\` | sí | sí |

## 17. Evidencia de pruebas

Cobertura unitaria en \`tests/unit/kyc-didit-system.test.ts\`: mapeo de estados, verificación de
firma, ventana anti-replay, idempotencia, minimización de datos y el switch de plataforma
\`legal_kyc_gate\`. El E2E manual (Owner → Didit → webhook → estado) se corre a mano por ahora
— no hay automation de cámara/documento en CI.
`,
  },
];

export const HELP_ARTICLES: HelpArticle[] = [
  ...CORE_HELP_ARTICLES,
  ...INTEGRATION_HELP_ARTICLES,
  ...PRINTING_HELP_ARTICLES,
  ...ADMIN_HELP_ARTICLES,
  ...GASTRONOMIA_HELP_ARTICLES,
  {
    id: "email-automations",
    title: "Automatizaciones de email (activar / desactivar)",
    section: "Notificaciones",
    sectionIcon: "✉️",
    keywords: [
      "automatización",
      "email automático",
      "carrito abandonado",
      "reengagement",
      "onboarding",
      "desactivar email",
    ],
    url: "/app/settings",
    content: `
Desde **Configuración → Notificaciones → Automatizaciones de email** podés controlar qué emails automáticos se envían.

**Automatizaciones disponibles:**

| Automatización | Qué hace |
|----------------|---------|
| **Carrito abandonado** | Email al cliente 2h después de iniciar un pedido sin completarlo (hasta 48h) |
| **Re-engagement** | Email al dueño cuando la tienda no recibió pedidos en los últimos 30 días |
| **Nudge de configuración** | Recordatorio si el setup no se completó en los primeros 7 días |

**Por defecto:** todas están activas. Desactivar una automatización la apaga para todos los clientes.

💡 Los cambios se aplican al próximo ciclo del cron (máx. 2h para carrito abandonado, 24h para los demás).
`,
  },
  {
    id: "resend-key-validation",
    title: "Verificar la clave de Resend",
    section: "Notificaciones",
    sectionIcon: "✉️",
    keywords: ["resend", "api key", "verificar", "validar", "email", "clave", "dominio"],
    url: "/app/settings",
    content: `
En **Configuración → Notificaciones → Proveedor de email** podés verificar tu clave de Resend en tiempo real.

**Pasos:**
1. Ingresá tu **API Key** de Resend (empieza con \`re_\`)
2. Hacé clic en **Verificar clave**
3. El sistema consulta la API de Resend y muestra:
   - ✓ **Válida** — indica cuántos dominios verificados tenés
   - ✗ **Inválida** — la clave no es correcta o fue revocada

**Si aún no tenés dominios verificados:** la clave puede ser válida pero el envío a destinatarios externos puede fallar. Verificá un dominio en tu panel de Resend antes de activar el envío masivo.

💡 Structura no guarda la clave en texto plano — se cifra al guardar y nunca se muestra completa.
`,
  },
  {
    id: "clear-demo-data",
    title: "Limpiar datos de muestra del catálogo",
    section: "Configuración",
    sectionIcon: "⚙️",
    keywords: ["demo", "datos de muestra", "limpiar", "borrar productos", "seed", "catálogo vacío"],
    url: "/app/dashboard",
    content: `
Si cargaste datos de muestra para explorar el producto, podés eliminarlos desde el **Panel de inicio**.

**Cómo limpiar el catálogo:**
1. Andá al **Panel de inicio** (\`/app/dashboard\`)
2. En el bloque de estado del catálogo, hacé clic en **Limpiar datos de muestra**
3. Confirmá la acción en el diálogo

**Qué se elimina:** todos los productos y categorías sin pedidos reales asociados.

**Restricción:** si ya tenés pedidos confirmados (no cancelados), la acción no está disponible para proteger el historial.

Después de limpiar, podés agregar tu catálogo real desde **Productos → Nuevo producto**.
`,
  },
  {
    id: "renata-os-overview",
    title: "Renata — Asistente de IA para tu negocio",
    section: "Renata",
    sectionIcon: "🤖",
    keywords: ["renata", "ia", "inteligencia artificial", "chat", "asistente", "ai"],
    url: "/app/renata",
    content: `
**Renata** es la capa de inteligencia artificial de tu workspace. Centraliza cuatro agentes especializados:

- **Customer AI** — responde preguntas de clientes sobre tu catálogo, horarios y entregas.
- **Executive AI** — analiza métricas, sugiere acciones operativas y genera reportes.
- **Supplier AI** — asiste en gestión de proveedores y órdenes de compra.
- **Experience AI** — optimiza el storefront y sugiere mejoras de conversión.

Activar Renata requiere plan Pro o superior. Las respuestas utilizan tu contexto de negocio (productos, pedidos, configuración) para ser relevantes.

**Primeros pasos:**
1. Activá el feature desde Configuración → Integraciones → Renata
2. Ingresá las credenciales de proveedor IA en Admin → Operaciones → Proveedores IA
3. El Customer AI empieza a responder preguntas de clientes automáticamente
`,
  },
  {
    id: "renata-agents",
    title: "Renata — Especialistas disponibles",
    section: "Renata",
    sectionIcon: "🤖",
    keywords: ["renata", "agentes", "customer ai", "executive ai", "supplier ai", "experience ai"],
    url: "/app/renata/agents",
    content: `
Cada especialista de Renata tiene un rol específico:

| Especialista | Para qué sirve |
|--------|----------------|
| **Customer AI** | Chat de atención al cliente en tu tienda pública |
| **Executive AI** | Análisis de ventas, alertas operativas y sugerencias |
| **Supplier AI** | Gestión de compras, recepción de mercadería y costos |
| **Experience AI** | Optimización del storefront y conversión |

Podés activar o desactivar cada especialista individualmente sin afectar a los demás.
`,
  },
  {
    id: "renata-knowledge",
    title: "Renata — Biblioteca / conocimiento",
    section: "Renata",
    sectionIcon: "🤖",
    keywords: ["renata", "conocimiento", "contexto", "training", "datos"],
    url: "/app/renata/knowledge",
    content: `
La **base de conocimiento** de Renata es el contexto que alimenta a los especialistas:

- **Catálogo y pedidos:** se indexan cuando aprobás candidatos de aprendizaje o cuando la indexación está configurada — no hay sincronización continua automática.
- **Manual:** podés agregar FAQs, políticas de cambio/devolución, y datos adicionales del negocio.
- **Documentos:** subí PDFs o texto libre (menús, guías, manuales internos).

**Actualizaciones:** después de aprobar conocimiento o reindexar, Renata usa el material en las siguientes consultas. No es un re-entreno continuo en background.
`,
  },
  {
    id: "renata-packs",
    title: "Renata — Configuración del negocio",
    section: "Renata",
    sectionIcon: "🤖",
    keywords: ["renata", "packs", "tokens", "capacidad", "límite"],
    url: "/app/renata/packs",
    content: `
Las **configuraciones de Renata** adaptan capacidades a tu tipo de negocio:

- **Pack Conversaciones:** aumenta el límite mensual de chats de Customer AI.
- **Pack Análisis Pro:** desbloquea insights avanzados en Executive AI.
- **Pack Documentos:** permite subir más documentos a la base de conocimiento.

Los packs se activan desde **Renata → Configuración del negocio** y se facturan mensualmente junto a tu plan.

Si alcanzás el límite de tokens configurado, Renata deja de responder hasta el próximo mes o hasta que ajustes el límite desde Admin.
`,
  },
  {
    id: "renata-automation",
    title: "Renata — Automatizaciones",
    section: "Renata",
    sectionIcon: "🤖",
    keywords: ["renata", "automatización", "flows", "trigger", "acción automática"],
    url: "/app/renata/automatizaciones",
    content: `
Renata puede proponer acciones cuando ocurre algo en el negocio. Las automatizaciones piden tu aprobación antes de ejecutar:

**Ejemplos de triggers con IA:**
- Cliente pregunta por devoluciones → Renata responde + crea ticket de soporte
- Pedido con baja valoración → Executive AI genera alerta operativa
- Stock crítico detectado → Supplier AI sugiere orden de recompra

**Cómo configurar:**
1. Andá a **Automatizaciones Renata** (/app/renata/automatizaciones)
2. Creá o abrí una automatización y describí la condición (ej. stock bajo) y la propuesta que querés revisar
3. Configurá el disparador y las condiciones
4. Definí la acción propuesta (email, notificación, crear tarea) — siempre pasa por tu aprobación; no hay ejecutor continuo en background
`,
  },
  {
    id: "renata-store",
    title: "Renata — Tienda de extensiones",
    section: "Renata",
    sectionIcon: "🤖",
    keywords: ["renata", "tienda", "store", "plugins", "extensiones"],
    url: "/app/renata/store",
    content: `
La **tienda de Renata** ofrece **packs declarativos** (instalás y configurás estado; no ejecutan hooks ni código en runtime):

- **Packs de canal / proveedor:** plantillas declarativas (p.ej. WhatsApp, Slack, Intercom) — no son conectores live cableados hasta que exista integración real aparte.
- **Skills / capacidades:** lógica de respuesta adaptada a tu industria (gastronomía, indumentaria, etc.) cuando el pack lo declare.
- **Integraciones externas (CRM/ERP/logística):** packs de configuración; **Instalada ≠ hooks ejecutados**.

Las extensiones premium, si aplican, se facturan aparte del plan base.
`,
  },
  {
    id: "hardware-setup",
    title: "Hardware — Configuración de dispositivos",
    section: "Hardware",
    sectionIcon: "🖥️",
    keywords: ["hardware", "dispositivos", "impresora", "balanza", "barcode", "qz tray", "pos"],
    url: "/app/hardware",
    content: `
El módulo **Hardware** conecta dispositivos físicos a tu workspace:

**Dispositivos compatibles:**
- Impresoras térmicas (comandas, tickets, etiquetas)
- Balanzas de mostrador (pesaje en tiempo real)
- Lectores de código de barras y QR
- Cajones de efectivo (apertura automática)
- Pantallas de cliente

**Requisitos:**
- **QZ Tray** instalado en la PC de caja (gratis, descarga en qz.io)
- Dispositivos conectados por USB, red o Bluetooth según el driver

**Pasos para activar:**
1. Andá a **Hardware → Dispositivos → Nuevo dispositivo**
2. Elegí el tipo y driver correspondiente
3. Ejecutá la prueba de conexión desde **Hardware → Pruebas**
4. Asigná el dispositivo a un rol (caja, cocina, depósito) en **Hardware → Asignaciones**
`,
  },
  {
    id: "mp-sandbox-vs-prod",
    title: "MercadoPago: modo prueba vs producción",
    section: "Configuración",
    sectionIcon: "⚙️",
    keywords: ["mercado pago", "sandbox", "prueba", "producción", "test", "live", "ambiente"],
    url: "/app/settings",
    content: `
En **Configuración → Pagos** la tarjeta de MercadoPago muestra el ambiente activo:

- **🔬 Modo prueba** (ámbar) — las credenciales son de tipo \`TEST-\`. Los pagos no son reales.
- **✓ Conectado** (verde) — las credenciales son de tipo \`APP_USR-\`. Los pagos son reales.

**Para pasar a producción:**
1. En tu cuenta de MercadoPago, generá las credenciales de producción (no de prueba)
2. Reemplazá el **Access Token** y la **Public Key** en la configuración
3. El badge cambia automáticamente a verde

⚠️ Nunca uses credenciales de prueba para cobros reales — los pagos en modo test no se acreditan.
`,
  },
];

export function searchHelpArticles(query: string): HelpArticle[] {
  return searchHelpArticlesRanked(query);
}

export const HELP_SECTIONS = [
  { id: "Primeros pasos", icon: "🚀" },
  { id: "Panel", icon: "🏠" },
  { id: "Pedidos", icon: "🛒" },
  { id: "Pesaje", icon: "⚖️" },
  { id: "Productos", icon: "📦" },
  { id: "Tienda pública", icon: "🌐" },
  { id: "B2B", icon: "🏫" },
  { id: "Operación", icon: "🏪" },
  { id: "Fidelización", icon: "⭐" },
  { id: "Configuración", icon: "⚙️" },
  { id: "Integraciones", icon: "💬" },
  { id: "Logística", icon: "🚚" },
  { id: "Notificaciones", icon: "🔔" },
  { id: "Impresoras", icon: "🖨️" },
  { id: "FAQ", icon: "❓" },
  { id: "Admin — Planes", icon: "🛡️" },
  { id: "POS", icon: "🏧" },
  { id: "Salón", icon: "🍽️" },
  { id: "Ingredientes", icon: "🧪" },
  { id: "Compras", icon: "🚚" },
  { id: "Renata", icon: "🤖" },
  { id: "Hardware", icon: "🖥️" },
  { id: "Legal y KYC", icon: "🪪" },
] as const;
